import React, { useMemo, useState } from "react";
import {
  Search,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  Loader2,
  AlertTriangle,
  MapPin,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import useCountrySenders from "../../../hooks/countrySenders/useCountrySenders";
import useCountries from "../../../hooks/countries/useCountries";
import { Country, CountrySender, CountrySenderDestination, CountrySenderStatus } from "../../../utils/database";

const StatusBadge: React.FC<{ status: CountrySenderStatus }> = ({ status }) => (
  <span
    className={`px-2 py-1 rounded-full text-xs font-medium ${
      status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {status === "ACTIVE" ? "Actif" : "Inactif"}
  </span>
);

const CountryInfo: React.FC<{ country?: Country }> = ({ country }) => {
  if (!country) return <span className="text-gray-400">—</span>;
  return (
    <div>
      <p className="font-medium text-gray-800">{country.name}</p>
      <p className="text-xs text-gray-500">
        {country.code} · +{country.phonecode} · {country.devise}
      </p>
    </div>
  );
};

const DeleteConfirmModal: React.FC<{
  title: string;
  message: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ title, message, isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-6">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

const SenderFormModal: React.FC<{
  isOpen: boolean;
  isCreating: boolean;
  sender: CountrySender | null;
  countries: Country[];
  usedCountryIds: number[];
  onClose: () => void;
  onSave: (data: { country_id: number; status: CountrySenderStatus; default_daily_limit: number | null }) => void;
  isLoading: boolean;
}> = ({ isOpen, isCreating, sender, countries, usedCountryIds, onClose, onSave, isLoading }) => {
  const [countryId, setCountryId] = useState(0);
  const [status, setStatus] = useState<CountrySenderStatus>("ACTIVE");
  const [dailyLimit, setDailyLimit] = useState("");

  React.useEffect(() => {
    if (isCreating) {
      setCountryId(0);
      setStatus("ACTIVE");
      setDailyLimit("");
    } else if (sender) {
      setCountryId(sender.country_id);
      setStatus(sender.status);
      setDailyLimit(
        sender.default_daily_limit !== null && sender.default_daily_limit !== undefined
          ? String(sender.default_daily_limit)
          : ""
      );
    }
  }, [isCreating, sender, isOpen]);

  if (!isOpen) return null;

  const availableCountries = isCreating
    ? countries.filter((c) => c.id && !usedCountryIds.includes(Number(c.id)))
    : countries;

  const selectedCountry =
    availableCountries.find((c) => Number(c.id) === countryId) || countries.find((c) => Number(c.id) === countryId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isCreating ? "Ajouter un pays d'envoi" : "Modifier le pays d'envoi"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!countryId) {
              alert("Veuillez sélectionner un pays");
              return;
            }
            const parsed = dailyLimit.trim() === "" ? null : Number(dailyLimit.replace(",", "."));
            if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
              alert("La limite journalière doit être un nombre positif");
              return;
            }
            onSave({
              country_id: countryId,
              status,
              default_daily_limit: parsed,
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'envoi *</label>
            <select
              value={countryId}
              onChange={(e) => setCountryId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
              disabled={isLoading || !isCreating}
            >
              <option value={0}>-- Sélectionner un pays --</option>
              {availableCountries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - {c.devise}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut *</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CountrySenderStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
              disabled={isLoading}
            >
              <option value="ACTIVE">Actif</option>
              <option value="INACTIVE">Inactif</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Limite journalière par défaut
              {selectedCountry?.devise ? ` (${selectedCountry.devise})` : ""}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
              placeholder="Ex: 2500"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Appliquée automatiquement aux nouveaux wallets de ce pays. Laisser vide pour utiliser le plafond système.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isCreating ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DestinationsModal: React.FC<{
  sender: CountrySender | null;
  countries: Country[];
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: { country_destination_id: number; status: CountrySenderStatus }) => Promise<void>;
  onToggleStatus: (destination: CountrySenderDestination) => Promise<void>;
  onDelete: (destination: CountrySenderDestination) => void;
  isLoading: boolean;
}> = ({ sender, countries, isOpen, onClose, onAdd, onToggleStatus, onDelete, isLoading }) => {
  const [destinationId, setDestinationId] = useState(0);
  const [status, setStatus] = useState<CountrySenderStatus>("ACTIVE");

  React.useEffect(() => {
    if (isOpen) {
      setDestinationId(0);
      setStatus("ACTIVE");
    }
  }, [isOpen, sender?.id]);

  if (!isOpen || !sender) return null;

  const existingDestinationIds = new Set((sender.destinations || []).map((d) => d.country_destination_id));

  const availableCountries = countries.filter(
    (c) => c.id && Number(c.id) !== sender.country_id && !existingDestinationIds.has(Number(c.id))
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Destinations autorisées</h2>
            <p className="text-sm text-gray-500 mt-1">
              Depuis <strong>{sender.country?.name || "—"}</strong>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Ajouter une destination</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={destinationId}
              onChange={(e) => setDestinationId(Number(e.target.value))}
              className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
              disabled={isLoading}
            >
              <option value={0}>-- Sélectionner un pays --</option>
              {availableCountries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - {c.devise}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CountrySenderStatus)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
              disabled={isLoading}
            >
              <option value="ACTIVE">Actif</option>
              <option value="INACTIVE">Inactif</option>
            </select>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!destinationId) {
                alert("Veuillez sélectionner une destination");
                return;
              }
              await onAdd({ country_destination_id: destinationId, status });
              setDestinationId(0);
              setStatus("ACTIVE");
            }}
            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Ajouter
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Destination</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(sender.destinations || []).length > 0 ? (
                sender.destinations!.map((destination) => (
                  <tr key={destination.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <CountryInfo country={destination.country_destination} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={destination.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onToggleStatus(destination)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title={destination.status === "ACTIVE" ? "Désactiver" : "Activer"}
                          disabled={isLoading}
                        >
                          {destination.status === "ACTIVE" ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(destination)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Supprimer"
                          disabled={isLoading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Aucune destination configurée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CountrySendersTab: React.FC = () => {
  const {
    countrySenders,
    countrySendersLoading,
    createCountrySender,
    updateCountrySender,
    deleteCountrySender,
    addDestination,
    updateDestination,
    deleteDestination,
  } = useCountrySenders();
  const { countries } = useCountries({});

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [editingSender, setEditingSender] = useState<CountrySender | null>(null);
  const [managingSender, setManagingSender] = useState<CountrySender | null>(null);
  const [deletingSender, setDeletingSender] = useState<CountrySender | null>(null);
  const [deletingDestination, setDeletingDestination] = useState<CountrySenderDestination | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const itemsPerPage = 10;

  const usedCountryIds = useMemo(() => countrySenders.map((s) => s.country_id), [countrySenders]);

  const filteredSenders = countrySenders.filter((sender) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (sender.country?.name || "").toLowerCase().includes(term) ||
      (sender.country?.code || "").toLowerCase().includes(term) ||
      (sender.country?.devise || "").toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredSenders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentSenders = filteredSenders.slice(startIndex, startIndex + itemsPerPage);

  const activeDestinationsCount = (sender: CountrySender) =>
    (sender.destinations || []).filter((d) => d.status === "ACTIVE").length;

  const handleSaveSender = async (data: {
    country_id: number;
    status: CountrySenderStatus;
    default_daily_limit: number | null;
  }) => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await createCountrySender(data);
      } else if (editingSender?.id) {
        await updateCountrySender(editingSender.id, {
          status: data.status,
          default_daily_limit: data.default_daily_limit,
        });
      }
      setEditingSender(null);
      setIsCreating(false);
    } catch (error) {
      console.error(error);
      alert(isCreating ? "Erreur lors de la création" : "Erreur lors de la modification");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSender = async () => {
    if (!deletingSender?.id) return;
    setIsSaving(true);
    try {
      await deleteCountrySender(deletingSender.id);
      setDeletingSender(null);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la suppression");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDestination = async (data: { country_destination_id: number; status: CountrySenderStatus }) => {
    if (!managingSender?.id) return;
    setIsSaving(true);
    try {
      await addDestination(managingSender.id, data);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de l'ajout de la destination");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleDestinationStatus = async (destination: CountrySenderDestination) => {
    if (!destination.id) return;
    setIsSaving(true);
    try {
      const nextStatus: CountrySenderStatus = destination.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      await updateDestination(destination.id, { status: nextStatus });
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la mise à jour du statut");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDestination = async () => {
    if (!deletingDestination?.id) return;
    setIsSaving(true);
    try {
      await deleteDestination(deletingDestination.id);
      setDeletingDestination(null);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la suppression");
    } finally {
      setIsSaving(false);
    }
  };

  React.useEffect(() => {
    if (managingSender?.id) {
      const refreshed = countrySenders.find((s) => s.id === managingSender.id);
      if (refreshed) setManagingSender(refreshed);
    }
  }, [countrySenders, managingSender?.id]);

  if (countrySendersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des corridors d'envoi...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Pays d'envoi configurés</h3>
            <p className="text-3xl font-bold text-green-600">{countrySenders.length}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Corridors actifs</h3>
            <p className="text-3xl font-bold text-blue-600">
              {countrySenders.reduce((sum, s) => sum + activeDestinationsCount(s), 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par pays, code, devise..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsCreating(true);
              setEditingSender({ country_id: 0, status: "ACTIVE" });
            }}
            className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Ajouter pays d'envoi
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays d'envoi</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Limite journalière</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Statut</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Destinations</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentSenders.length > 0 ? (
                currentSenders.map((sender) => (
                  <tr key={sender.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <CountryInfo country={sender.country} />
                    </td>
                    <td className="px-6 py-4">
                      {sender.default_daily_limit != null ? (
                        <span className="font-medium text-gray-800">
                          {Number(sender.default_daily_limit).toLocaleString("fr-FR")}
                          <span className="text-xs text-gray-500 ml-1">{sender.country?.devise || ""}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={sender.status} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">
                        {activeDestinationsCount(sender)} actif(s) / {(sender.destinations || []).length} total
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsCreating(false);
                            setEditingSender(sender);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Modifier le statut"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setManagingSender(sender)}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Gérer les destinations"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingSender(sender)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    Aucun pays d'envoi configuré
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredSenders.length)} sur{" "}
              {filteredSenders.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <SenderFormModal
        isOpen={!!editingSender}
        isCreating={isCreating}
        sender={editingSender}
        countries={countries || []}
        usedCountryIds={usedCountryIds}
        onClose={() => {
          setEditingSender(null);
          setIsCreating(false);
        }}
        onSave={handleSaveSender}
        isLoading={isSaving}
      />

      <DestinationsModal
        sender={managingSender}
        countries={countries || []}
        isOpen={!!managingSender}
        onClose={() => setManagingSender(null)}
        onAdd={handleAddDestination}
        onToggleStatus={handleToggleDestinationStatus}
        onDelete={setDeletingDestination}
        isLoading={isSaving}
      />

      <DeleteConfirmModal
        title="Supprimer le pays d'envoi"
        message={`Êtes-vous sûr de vouloir supprimer ${deletingSender?.country?.name || "ce pays d'envoi"} et toutes ses destinations ?`}
        isOpen={!!deletingSender}
        onClose={() => setDeletingSender(null)}
        onConfirm={handleDeleteSender}
        isLoading={isSaving}
      />

      <DeleteConfirmModal
        title="Supprimer la destination"
        message={`Supprimer la destination vers ${deletingDestination?.country_destination?.name || "ce pays"} ?`}
        isOpen={!!deletingDestination}
        onClose={() => setDeletingDestination(null)}
        onConfirm={handleDeleteDestination}
        isLoading={isSaving}
      />
    </div>
  );
};

export default CountrySendersTab;
